// The platform layer: is this a phone or a desktop, does it have WebGL2, which quality tier does the game want for
// it; and the browser chores every web game needs: pause when the tab is hidden, pointer lock, full-screen with a
// landscape lock, and unlocking audio on the first gesture. The tier table is the game's value; this file only
// reads the device. Decisions: 0021 (the web, phone and desktop first-class), 0032 (platform boot).

/** The two device classes the engine designs for (0021). A tablet counts as a phone: touch first. */
export type DeviceKind = "phone" | "desktop";

/** What the device looks like. */
export interface PlatformInfo {
  kind: DeviceKind;
  /** The user agent says phone or tablet (iPadOS's desktop agent with touch counts). */
  mobileAgent: boolean;
  /** Touch points reported. */
  touch: boolean;
  /** The primary pointer is coarse (a finger). */
  coarsePointer: boolean;
  /** A WebGL2 context can be made. */
  webgl2: boolean;
  /** The user asked for less motion. */
  reducedMotion: boolean;
  devicePixelRatio: number;
  /** CSS pixels. */
  viewport: [number, number];
}

/** The inputs classifyDevice reads (plain values, so it tests without a browser). */
export interface DeviceSignals {
  userAgent: string;
  maxTouchPoints: number;
  coarsePointer: boolean;
  /** navigator.userAgentData.mobile where the browser has it. */
  uaMobile?: boolean;
}

/** Phone or desktop from the signals: a mobile agent, or a coarse primary pointer with touch, is a phone. */
export function classifyDevice(s: DeviceSignals): { kind: DeviceKind; mobileAgent: boolean } {
  const ipadAsMac = /Macintosh/.test(s.userAgent) && s.maxTouchPoints > 1;
  const mobileAgent = s.uaMobile === true || /Android|iPhone|iPad|iPod|Mobile|Silk|Kindle/i.test(s.userAgent) || ipadAsMac;
  const kind: DeviceKind = mobileAgent || (s.coarsePointer && s.maxTouchPoints > 0) ? "phone" : "desktop";
  return { kind, mobileAgent };
}

/** Whether a WebGL2 context can be made (a throwaway canvas; the context is released). */
export function hasWebGL2(doc: Document = document): boolean {
  try {
    const c = doc.createElement("canvas");
    const gl = c.getContext("webgl2");
    if (!gl) return false;
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return true;
  } catch {
    return false;
  }
}

/** Read the device from the browser. */
export function detectPlatform(win: Window = window): PlatformInfo {
  const nav = win.navigator as Navigator & { userAgentData?: { mobile?: boolean } };
  const mq = (q: string): boolean => win.matchMedia?.(q).matches ?? false;
  const coarsePointer = mq("(pointer: coarse)");
  const { kind, mobileAgent } = classifyDevice({ userAgent: nav.userAgent, maxTouchPoints: nav.maxTouchPoints ?? 0, coarsePointer, uaMobile: nav.userAgentData?.mobile });
  return {
    kind,
    mobileAgent,
    touch: (nav.maxTouchPoints ?? 0) > 0,
    coarsePointer,
    webgl2: hasWebGL2(win.document),
    reducedMotion: mq("(prefers-reduced-motion: reduce)"),
    devicePixelRatio: win.devicePixelRatio ?? 1,
    viewport: [win.innerWidth, win.innerHeight],
  };
}

/**
 * The quality tier for a device from the game's table (`{ phone: "low", desktop: "high" }`). An `override` (a URL
 * value the game read) wins when it names a known tier; an unknown one is ignored with a warning.
 */
export function pickTier<T extends string>(kind: DeviceKind, table: Readonly<Record<DeviceKind, T>>, o: { override?: string | null; tiers?: readonly T[] } = {}): { tier: T; source: "override" | "device" } {
  const known = o.tiers ?? (Object.values(table) as T[]);
  if (o.override != null) {
    if ((known as readonly string[]).includes(o.override)) return { tier: o.override as T, source: "override" };
    console.warn(`[boot] unknown tier "${o.override}" (have ${known.join(", ")}); using the device's`);
  }
  return { tier: table[kind], source: "device" };
}

/** Call `fn(hidden)` whenever the page is hidden or shown again; returns the remover. */
export function onVisibility(fn: (hidden: boolean) => void, doc: Document = document): () => void {
  const h = (): void => fn(doc.visibilityState === "hidden");
  doc.addEventListener("visibilitychange", h);
  return () => doc.removeEventListener("visibilitychange", h);
}

/** The events that count as a user gesture for audio, pointer lock and full-screen. */
export const GESTURE_EVENTS = ["pointerdown", "keydown", "touchend"] as const;

/**
 * Run `fn` once, on the first user gesture on `target` (a press, a key, a tap), inside the event handler so the
 * browser's user-activation rules hold for audio, pointer lock and full-screen. Returns the remover.
 */
export function onFirstGesture(fn: (e: Event) => void, target: EventTarget = window): () => void {
  let fired = false;
  const h = (e: Event): void => {
    if (fired) return;
    fired = true;
    off();
    fn(e);
  };
  const off = (): void => {
    for (const t of GESTURE_EVENTS) target.removeEventListener(t, h, true);
  };
  for (const t of GESTURE_EVENTS) target.addEventListener(t, h, true);
  return off;
}

/** An audio context, structurally (WebAudio's, or the game's mixer wrapper). */
export interface AudioContextLike {
  state: string;
  resume(): Promise<void>;
}

/** Resume a suspended audio context; true when it ends up running. Call it from a gesture handler. */
export async function unlockAudio(ctx: AudioContextLike): Promise<boolean> {
  try {
    if (ctx.state !== "running") await ctx.resume();
  } catch {
    return false;
  }
  return ctx.state === "running";
}

/** Ask for pointer lock on `el` (from a gesture handler); resolves true once locked, false if refused. */
export function requestPointerLock(el: Element, doc: Document = el.ownerDocument): Promise<boolean> {
  if (!el.requestPointerLock) return Promise.resolve(false);
  return new Promise((resolve) => {
    const done = (ok: boolean): void => {
      doc.removeEventListener("pointerlockchange", onChange);
      doc.removeEventListener("pointerlockerror", onError);
      resolve(ok);
    };
    const onChange = (): void => done(doc.pointerLockElement === el);
    const onError = (): void => done(false);
    doc.addEventListener("pointerlockchange", onChange);
    doc.addEventListener("pointerlockerror", onError);
    try {
      const r = el.requestPointerLock() as unknown as Promise<void> | undefined;
      r?.catch?.(() => done(false));
    } catch {
      done(false);
    }
  });
}

/**
 * Go full-screen on `el` (from a gesture handler), hiding the browser's navigation UI, and on a phone try to lock
 * landscape. Resolves true when full-screen; the orientation lock is best effort (iOS has none).
 */
export async function enterFullscreen(el: Element, o: { landscape?: boolean } = {}): Promise<boolean> {
  const doc = el.ownerDocument;
  try {
    if (!doc.fullscreenElement && el.requestFullscreen) await el.requestFullscreen({ navigationUI: "hide" });
  } catch {
    return false;
  }
  if (o.landscape) {
    const orientation = (globalThis.screen as Screen | undefined)?.orientation as (ScreenOrientation & { lock?: (o: string) => Promise<void> }) | undefined;
    await orientation?.lock?.("landscape").catch(() => undefined);
  }
  return !!doc.fullscreenElement;
}
